/**
 * "Investigator finished" — the thing that was missing entirely.
 *
 * A background run used to announce itself by quietly changing a chip above a
 * composer the user had to already be looking at. Anyone who launched a run
 * and went to read something else learned nothing, ever.
 *
 * Rendered above the router so it shows on any page. It does NOT steal focus
 * or block: a run finishing is information, not an interruption, and a modal
 * over the document someone is reading would be worse than the silence it
 * replaces.
 *
 * It reports the outcome only; it never attaches anything. A completed run
 * points the user at CEPI ("Use it from CEPI."), where the attach is their
 * explicit choice. Failed / cancelled are said out loud, because a dead run
 * that stays silent is indistinguishable from one still working.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobWatcher } from '../contexts/JobWatcherContext';
import { outcomeLine, progressLine, type FinishedJob } from '../services/watchedJobs';

const TONE: Record<FinishedJob['outcome'], string> = {
  complete: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  failed: 'border-red-300 bg-red-50 text-red-900',
  cancelled: 'border-slate-300 bg-slate-50 text-slate-800',
};

export const JobToasts: React.FC = () => {
  const watcher = useJobWatcher();
  const navigate = useNavigate();
  if (!watcher) return null;

  // One job, one toast. A poll can land a job in `finished` on the same
  // render it is still listed as running; the outcome wins.
  const done = new Set(watcher.finished.map((j) => j.jobId));
  const running = (watcher.running ?? []).filter((j) => !done.has(j.jobId));
  if (!running.length && !watcher.finished.length) return null;

  return (
    // TOP right below `lg`, bottom LEFT from `lg` up. Bottom right sat directly
    // over the CEPI composer, so anyone who launched a run could not see what
    // they were typing while it ran (reported live 2026-09-24). CEPI is now the
    // right-hand column at `lg`+, where the bottom-left corner is the workspace;
    // below `lg` the composer is the full-width Chat view, so the toast stays at
    // the top. No unprefixed bottom-* class: it would reintroduce that overlap.
    <div
      className="pointer-events-none fixed top-4 right-4 lg:top-auto lg:right-auto lg:bottom-4 lg:left-4 z-[90] flex w-80
                 flex-col gap-2"
      data-testid="job-toasts"
      role="status"
      aria-live="polite"
    >
      {/* In-flight runs, above the finished ones. No dismiss: the only live
          indicator for a job must not be closeable while the job carries on —
          that is the silence this component exists to remove, one step later
          in the lifecycle. */}
      {running.map((job) => {
        const live = watcher.progress?.[job.jobId];
        return (
          <div
            key={job.jobId}
            data-testid={`job-running-${job.jobId}`}
            className="pointer-events-auto rounded-lg border border-indigo-300
                       bg-indigo-50 px-3 py-2.5 text-indigo-900 shadow-lg"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-indigo-500"
                aria-hidden="true"
              />
              <p className="text-[12px] font-semibold">Investigator · running</p>
            </div>
            <p className="mt-0.5 truncate text-[11px] opacity-80" title={job.criterion}>
              {job.criterion || 'Untitled run'}
            </p>
            <p className="mt-1 text-[11px] opacity-90">
              {live ? progressLine(live) : 'Starting…'}
            </p>
          </div>
        );
      })}
      {watcher.finished.map((job) => (
        <div
          key={job.jobId}
          data-testid={`job-toast-${job.jobId}`}
          className={`pointer-events-auto rounded-lg border px-3 py-2.5 shadow-lg
                      ${TONE[job.outcome]}`}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold">
                Investigator · {outcomeLine(job)}
              </p>
              <p className="mt-0.5 truncate text-[11px] opacity-80"
                 title={job.criterion}>
                {job.criterion || 'Untitled run'}
              </p>
              {job.outcome === 'complete' && (
                <p className="mt-1 text-[11px] opacity-90">
                  Use it from CEPI.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => watcher.dismiss(job.jobId)}
              aria-label="Dismiss"
              data-testid={`job-toast-dismiss-${job.jobId}`}
              className="rounded px-1 text-[13px] leading-none opacity-60 hover:opacity-100"
            >
              &times;
            </button>
          </div>
          {job.outcome !== 'cancelled' && (
            <button
              type="button"
              onClick={() => { watcher.dismiss(job.jobId); navigate(`/research/${job.jobId}`); }}
              data-testid={`job-toast-open-${job.jobId}`}
              className="mt-2 rounded border border-current/30 px-2 py-1 text-[11px]
                         font-medium hover:bg-black/5"
            >
              View run
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default JobToasts;
